// ABOUTME: Root layout component providing app shell structure.
// ABOUTME: Includes Pinboard sidebar with animations.

import { Outlet, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HelpCircle } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { TourProvider, useTour } from '@/context/TourContext';
import { Pinboard } from './Pinboard';
import { Toolbar } from './Toolbar';
import { AppTour } from '@/components/tour/AppTour';

function TourHelpButton() {
  const { restartTour, isRunning } = useTour();
  const location = useLocation();

  // Hide help button on onboarding page (user is still getting set up)
  if (location.pathname === '/onboarding') {
    return null;
  }

  // Hide help button on report wizard and report editor (prevents overlap with editor panel footers)
  // Exception: always show during tour so the final step can highlight it
  if (!isRunning && (location.pathname === '/reports/new' || location.pathname.match(/^\/reports\/[^/]+$/))) {
    return null;
  }

  return (
    <button
      onClick={restartTour}
      className="fixed bottom-6 right-6 z-50 group"
      aria-label="Restart tour"
      title="Restart tour"
      data-tour="help-button"
    >
      <div className="relative flex items-center justify-center w-12 h-12 rounded-full bg-obsidian-void/80 border border-obsidian-border/40 backdrop-blur-sm transition-all duration-200 hover:bg-obsidian-void hover:border-obsidian-border hover:scale-110 shadow-lg">
        <HelpCircle className="w-5 h-5 text-obsidian-text-secondary group-hover:text-obsidian-text-primary transition-colors" />
      </div>
    </button>
  );
}

export function RootLayout() {
  const { isPinboardOpen } = useApp();

  return (
    <TourProvider>
      <div className="relative w-full h-screen overflow-hidden bg-surface-50 flex selection:bg-primary-500/30 selection:text-surface-900">
        <motion.div
          className="relative h-full flex-1 flex flex-col overflow-hidden bg-surface-50 shadow-xl z-10"
          initial={false}
          animate={{
            marginRight: isPinboardOpen ? 320 : 0,
            scale: isPinboardOpen ? 0.98 : 1,
            borderRadius: isPinboardOpen ? '8px' : '0px',
          }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          {/* Top Toolbar */}
          <Toolbar />

          {/* Main Content Area */}
          <div className="flex-1 overflow-hidden">
            <Outlet />
          </div>
        </motion.div>

        {/* Pinboard Sidebar */}
        <Pinboard />

        {/* Onboarding Tour */}
        <AppTour />

        {/* Tour Help Button */}
        <TourHelpButton />
      </div>
    </TourProvider>
  );
}
